import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Pencil,
  Trash2,
  LogOut,
} from "lucide-react";
import "../Styles/dashgallery.css";
import Swal from "sweetalert2";
import imageCompression from "browser-image-compression";


// Change this if your backend runs on a different host/port
const API_BASE_URL = "";



const TABS = [
  {
    key: "add",
    label: "Add Event",
    icon: "/assets/dbg_icon_1.png",
  },
  {
    key: "upload",
    label: "Upload Event",
    icon: "/assets/dbg_icon_2.png",
  },
  {
    key: "manage",
    label: "Manage Gallery",
    icon: "/assets/dbg_icon_3.png",
  },
];

const ITEMS_PER_PAGE = 100;

export default function DashGallery() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("add");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // ---- Upload Event Image tab state ----
  const [uploadEventId, setUploadEventId] = useState("");
  // CHANGED: now holds an ARRAY of files instead of a single file
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // ---- simple "Edit / Add" modal — used ONLY on the Add Event tab ----
  // eventModal = null (closed) OR { mode: 'add' | 'edit', id: number|null, eventName: string }
  const [eventModal, setEventModal] = useState(null);

  // ---- Manage Gallery edit modal state — now targets ONE image row ----
  // editTarget = null OR { eventId, eventName, imageId: number|null, imageUrl: string|null }
  const [editTarget, setEditTarget] = useState(null);
  const [editEventName, setEditEventName] = useState("");
  const [editFile, setEditFile] = useState(null);

  // ---- Delete modal state ----
  // deleteTarget = null OR { type: 'event', id } OR { type: 'image', id, eventId }
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/gallery-events`);
      if (!res.ok) throw new Error("Failed to load events");
      const data = await res.json();
      setEvents(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setEvents([]);
      setError("Failed to load events. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, activeTab]);

  const filteredEvents = events.filter((e) =>
    e.eventName?.toLowerCase().includes(query.toLowerCase())
  );

  // ------------------------------------------------------------
  // Flatten events -> one row PER IMAGE for the Manage Gallery tab.
  // CHANGED: events with NO images yet are skipped entirely (return [])
  // instead of showing a placeholder row. This means a newly added
  // event will NOT appear in Manage Gallery until at least one image
  // has actually been uploaded to it.
  // ------------------------------------------------------------
  const manageRows = filteredEvents.flatMap((event) =>
    event.images && event.images.length > 0
      ? event.images.map((img) => ({
          eventId: event.id,
          eventName: event.eventName,
          imageId: img.id,
          imageUrl: img.imageUrl,
        }))
      : []
  );

  // Rows to actually render depend on the active tab:
  // - "manage" -> flattened image rows
  // - "add"    -> one row per event (no images shown)
  const displayRows = activeTab === "manage" ? manageRows : filteredEvents;

  const totalPages = Math.max(1, Math.ceil(displayRows.length / ITEMS_PER_PAGE));
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedRows = displayRows.slice(pageStart, pageStart + ITEMS_PER_PAGE);

  // "Add New Event" button (Add Event tab only) opens the simple Edit/Add modal
  const handleAddNewEvent = () => {
    setEventModal({ mode: "add", id: null, eventName: "" });
  };

 // ------------------------------------------------------------
  // CHANGED: uploads each file as its OWN request, in parallel.
  // If one file fails (too big, wrong type, network blip), the
  // rest still succeed instead of the whole batch failing together.
  // ------------------------------------------------------------
 const handleUploadSubmit = async () => {
    if (!uploadEventId) {
      alert("Please select an event.");
      return;
    }
    if (!uploadFiles || uploadFiles.length === 0) {
      alert("Please choose at least one image to upload.");
      return;
    }

    setIsUploading(true);

    // Compress each file before sending — cuts upload size drastically,
    // which is what actually makes the upload faster.
    const compressedFiles = await Promise.all(
      uploadFiles.map(async (file) => {
        try {
          const compressed = await imageCompression(file, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          return new File([compressed], file.name, { type: compressed.type });
        } catch (err) {
          console.error("Compression failed for", file.name, err);
          return file;
        }
      })
    );

    const results = await Promise.allSettled(
      compressedFiles.map((file) => {
        const formData = new FormData();
        formData.append("images", file);
        return fetch(`${API_BASE_URL}/api/gallery-events/${uploadEventId}/images`, {
          method: "POST",
          body: formData,
        }).then(async (res) => {
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.message || `Failed: ${file.name}`);
          }
          return res.json();
        });
      })
    );

    const failed = results.filter((r) => r.status === "rejected");
    const succeeded = results.length - failed.length;

    // CHANGED: show the result immediately, don't wait for fetchEvents() first
    setUploadFiles([]);
    setUploadEventId("");
    setIsUploading(false);

    if (failed.length === 0) {
      Swal.fire({
        title: "Success!",
        text: `All ${succeeded} image${succeeded > 1 ? "s" : ""} uploaded successfully.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
      });
    } else if (succeeded === 0) {
      Swal.fire({
        title: "Error",
        text: `Failed to upload ${failed.length} image${failed.length > 1 ? "s" : ""}. Please try again.`,
        icon: "error",
      });
    } else {
      Swal.fire({
        title: "Partial Upload",
        text: `${succeeded} uploaded successfully, ${failed.length} failed. Please retry the failed ones.`,
        icon: "warning",
      });
    }

    // CHANGED: refresh in the background, no "await" — doesn't delay the popup
    fetchEvents();
  };

  // ---------------- simple Edit/Add modal logic (Add Event tab) ----------------
  const openSimpleEditModal = (event) => {
    setEventModal({ mode: "edit", id: event.id, eventName: event.eventName || "" });
  };

  const closeEventModal = () => {
    setEventModal(null);
  };

  const updateEventModalName = (value) => {
    setEventModal((prev) => (prev ? { ...prev, eventName: value } : prev));
  };

  const handleSaveEvent = async () => {
    if (!eventModal) return;

    if (!eventModal.eventName.trim()) {
      alert("Please enter an event name.");
      return;
    }

    try {
      if (eventModal.mode === "add") {
        const res = await fetch(`${API_BASE_URL}/api/gallery-events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventName: eventModal.eventName }),
        });
        if (!res.ok) throw new Error("Failed to add event");
        const created = await res.json();
        setEvents((prev) => [...prev, { ...created, images: created.images || [] }]);
      } else {
        const res = await fetch(`${API_BASE_URL}/api/gallery-events/${eventModal.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventName: eventModal.eventName }),
        });
        if (!res.ok) throw new Error("Failed to update event");
        const updated = await res.json();
        setEvents((prev) =>
          prev.map((e) => (e.id === eventModal.id ? { ...e, ...updated } : e))
        );
      }
      closeEventModal();
    } catch (err) {
      console.error(err);
      alert(
        eventModal.mode === "add"
          ? "Failed to add event. Please try again."
          : "Failed to update event. Please try again."
      );
    }
  };

  // ---------------- Manage Gallery edit modal — targets ONE image row ----------------
  const openEditModal = (row) => {
    setEditTarget(row);
    setEditEventName(row.eventName || "");
    setEditFile(null);
  };

  const closeEditModal = () => {
    setEditTarget(null);
    setEditEventName("");
    setEditFile(null);
  };

  const handleUpdateGallery = async () => {
    if (!editTarget) return;
    try {
      // 1. Update the event name (applies to the whole event)
      const nameRes = await fetch(
        `${API_BASE_URL}/api/gallery-events/${editTarget.eventId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ eventName: editEventName }),
        }
      );
      if (!nameRes.ok) throw new Error("Failed to update event name");

      // 2. If a new file was chosen, replace THIS image (or add one if the
      //    event had none yet).
      if (editFile) {
        const formData = new FormData();
        formData.append("image", editFile);

        if (editTarget.imageId) {
          const imgRes = await fetch(
            `${API_BASE_URL}/api/gallery-events/images/${editTarget.imageId}`,
            { method: "PUT", body: formData }
          );
          if (!imgRes.ok) throw new Error("Failed to update image");
        } else {
          // Placeholder row with no image yet — add it via the multi-upload route
          const multiFormData = new FormData();
          multiFormData.append("images", editFile);

          const imgRes = await fetch(
            `${API_BASE_URL}/api/gallery-events/${editTarget.eventId}/images`,
            { method: "POST", body: multiFormData }
          );
          if (!imgRes.ok) throw new Error("Failed to upload image");
        }
      }

      await fetchEvents(); // refresh everything from the server
      closeEditModal();
    } catch (err) {
      console.error(err);
      alert("Failed to update gallery. Please try again.");
    }
  };

  // ---------------- single Edit handler — routes to the correct modal based on active tab ----------------
  const handleEditClick = (row) => {
    if (activeTab === "manage") {
      openEditModal(row); // one image row
    } else {
      openSimpleEditModal(row); // whole event (Add Event tab)
    }
  };

  // ---------------- Delete modal ----------------
  const openDeleteModal = (row) => {
    if (activeTab === "manage") {
      setDeleteTarget({ type: "image", id: row.imageId, eventId: row.eventId });
    } else {
      setDeleteTarget({ type: "event", id: row.id });
    }
  };
  const closeDeleteModal = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "image") {
        if (!deleteTarget.id) {
          // placeholder row with no real image — nothing to delete server-side
          closeDeleteModal();
          return;
        }
        const res = await fetch(
          `${API_BASE_URL}/api/gallery-events/images/${deleteTarget.id}`,
          { method: "DELETE" }
        );
        if (!res.ok) throw new Error("Failed to delete image");
        await fetchEvents();
      } else {
        const res = await fetch(
          `${API_BASE_URL}/api/gallery-events/${deleteTarget.id}`,
          { method: "DELETE" }
        );
        if (!res.ok) throw new Error("Failed to delete");
        setEvents((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete. Please try again.");
    } finally {
      closeDeleteModal();
    }
  };

  const showImageColumn = activeTab === "manage";
  const showPagination = activeTab === "manage";
  // CHANGED: Search box + Add New Event button should only show on the Add Event tab,
  // not on Manage Gallery.
  const showControls = activeTab !== "manage";

  return (
    <div className="dgl-app">
      {/* ---------------- CHANGED: Sidebar removed as requested ---------------- */}

      {/* ---------------- Main content ---------------- */}
      <main className="dgl-main">
        <header className="dgl-topbar">
          <h1>Gallery Management</h1>
          <button className="dgl-logout">
            <LogOut size={14} />
            Logout
          </button>
        </header>

        {/* Tab bar */}
        <div className="dgl-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`dgl-tab${activeTab === tab.key ? " active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <img
                src={tab.icon}
                alt={tab.label}
                className="dgl-tab-icon"
              />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ---------------- Add New Event / Manage Gallery (table view) ---------------- */}
        {(activeTab === "add" || activeTab === "manage") && (
          <section className="dgl-panel">
            {/* Controls row — CHANGED: hidden on Manage Gallery tab */}
            {showControls && (
              <div className="dgl-controls">
                <div className="dgl-search">
                  <Search size={14} />
                  <input
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
                <button className="dgl-add-btn" onClick={handleAddNewEvent}>
                  <img
                      src="/assets/dbg_icon_6.png"
                      alt="Add"
                      className="dgl-add-icon"
                  />
                  Add New Event
              </button>
              </div>
            )}

            {/* Table */}
            <div className="dgl-table-wrap">
              {loading ? (
                <p className="dgl-state-msg">Loading events...</p>
              ) : error ? (
                <p className="dgl-state-msg dgl-state-msg--error">{error}</p>
              ) : (
                <table className="dgl-table">
                  <thead>
                    <tr>
                      <th className="dgl-sno-col">S.No</th>
                      {showImageColumn && <th className="dgl-image-col">Image</th>}
                      <th>Event Name</th>
                      <th className="dgl-action-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={showImageColumn ? 4 : 3} className="dgl-state-msg">
                          No events yet.
                        </td>
                      </tr>
                    ) : activeTab === "manage" ? (
                      // ---------------- Manage Gallery: one row PER IMAGE ----------------
                      paginatedRows.map((row, index) => (
                        <tr key={`${row.eventId}-${row.imageId ?? "none"}`}>
                          <td className="dgl-sno-col">{pageStart + index + 1}</td>
                          <td className="dgl-image-col">
                            <img
                              src={row.imageUrl || "/assets/gallery-sample.jpg"}
                              alt={row.eventName}
                              className="dgl-thumb"
                            />
                          </td>
                          <td>{row.eventName}</td>
                          <td className="dgl-action-col">
                            <div className="dgl-row-actions">
                              <button
                                className="dgl-icon-btn dgl-icon-btn--edit"
                                onClick={() => handleEditClick(row)}
                              >
                                <img
                                  src="/assets/dbg_icon_4.png"
                                  alt="Edit"
                                  className="dgl-action-icon"
                                />
                                Edit
                              </button>
                              <button
                                className="dgl-icon-btn dgl-icon-btn--delete"
                                onClick={() => openDeleteModal(row)}
                              >
                                <img
                                  src="/assets/dbg_icon_5.png"
                                  alt="Delete"
                                  className="dgl-action-icon"
                                />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      // ---------------- Add Event: one row PER EVENT ----------------
                      paginatedRows.map((e, index) => (
                        <tr key={e.id}>
                          <td className="dgl-sno-col">{pageStart + index + 1}</td>
                          <td>{e.eventName}</td>
                          <td className="dgl-action-col">
                            <div className="dgl-row-actions">
                              <button
                                className="dgl-icon-btn dgl-icon-btn--edit"
                                onClick={() => handleEditClick(e)}
                              >
                                <img
                                  src="/assets/dbg_icon_4.png"
                                  alt="Edit"
                                  className="dgl-action-icon"
                                />
                                Edit
                              </button>
                              <button
                                className="dgl-icon-btn dgl-icon-btn--delete"
                                onClick={() => openDeleteModal(e)}
                              >
                                <img
                                  src="/assets/dbg_icon_5.png"
                                  alt="Delete"
                                  className="dgl-action-icon"
                                />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {showPagination && !loading && !error && (
              <div className="dgl-pagination">
                <span>
                  Showing {displayRows.length === 0 ? 0 : pageStart + 1}-
                  {Math.min(pageStart + ITEMS_PER_PAGE, displayRows.length)} of{" "}
                  {displayRows.length} Members
                </span>
                <div className="dgl-page-controls">
                  <button
                    className="dgl-page-btn dgl-page-arrow"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => i + 1).map(
                    (p) => (
                      <button
                        key={p}
                        className={`dgl-page-btn${currentPage === p ? " active" : ""}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    )
                  )}
                  {totalPages > 3 && <span className="dgl-page-ellipsis">...</span>}
                  {totalPages > 3 && (
                    <button
                      className={`dgl-page-btn${currentPage === totalPages ? " active" : ""}`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  )}
                  <button
                    className="dgl-page-btn dgl-page-arrow"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ---------------- Upload Event Image (card view) ---------------- */}
        {activeTab === "upload" && (
          <section className="dgl-panel dgl-panel--upload">
            <div className="dgl-upload-card">
              <h2>Upload Events Images</h2>

              <label className="dgl-field-label">Select category:</label>
<div className="dgl-select-wrap">
  <select
    value={uploadEventId}
    onChange={(e) => setUploadEventId(e.target.value)}
  >
    <option value="">-Select Event-</option>
    {events.map((ev) => (
      <option key={ev.id} value={ev.id}>
        {ev.eventName}
      </option>
    ))}
  </select>
  <ChevronDown size={14} className="dgl-select-chevron" />
</div>

            <label className="dgl-field-label">Select Images to Upload:</label>
              <label className="dgl-file-input">
                <span className="dgl-file-btn">Choose Files</span>
                <span className="dgl-file-name">
                  {uploadFiles.length > 0
                    ? `${uploadFiles.length} file${uploadFiles.length > 1 ? "s" : ""} selected`
                    : "No files Choosen"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) =>
                    setUploadFiles(e.target.files ? Array.from(e.target.files) : [])
                  }
                />
              </label>

              {uploadFiles.length > 0 && (
                <div className="dgl-selected-files-summary">
                  <span>{uploadFiles.length} image{uploadFiles.length > 1 ? "s" : ""} ready to upload</span>
                  <button
                    type="button"
                    className="dgl-clear-files-btn"
                    onClick={() => setUploadFiles([])}
                  >
                    Clear
                  </button>
                </div>
              )}

              <button
                className="dgl-primary-btn"
                onClick={handleUploadSubmit}
                disabled={isUploading}
              >
                {isUploading ? "Uploading..." : "Upload Images"}
              </button>
            </div>
          </section>
        )}
      </main>

      {/* ---------------- simple "Edit / Add" modal — Add Event tab only ---------------- */}
      {eventModal && (
        <div className="dgl-modal-overlay" onClick={closeEventModal}>
          <div className="dgl-modal-card dgl-event-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit / Add</h2>

            <label className="dgl-field-label">Event Name</label>
            <input
              type="text"
              className="dgl-event-input"
              value={eventModal.eventName}
              onChange={(e) => updateEventModalName(e.target.value)}
              autoFocus
            />

            <div className="dgl-event-modal-actions">
              <button className="dgl-btn-save" onClick={handleSaveEvent}>
                Save
              </button>
              <button className="dgl-btn-cancel-outline" onClick={closeEventModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- image-based Edit Modal — Manage Gallery tab only, edits ONE image ---------------- */}
      {editTarget && (
        <div className="dgl-modal-overlay" onClick={closeEditModal}>
          <div className="dgl-modal-card" onClick={(e) => e.stopPropagation()}>
            <h2>Manage Gallery</h2>

            <img
              src={editTarget.imageUrl || "/assets/gallery-sample.jpg"}
              alt={editTarget.eventName}
              className="dgl-modal-preview"
            />

            <label className="dgl-field-label">Select Event Name:</label>
            <div className="dgl-select-wrap">
              <select
                value={editEventName}
                onChange={(e) => setEditEventName(e.target.value)}
              >
                <option value="">-All-</option>
                {events.map((e) => (
                  <option key={e.id} value={e.eventName}>
                    {e.eventName}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="dgl-select-chevron" />
            </div>

            <label className="dgl-field-label">Select Image to Upload:</label>
            <label className="dgl-file-input">
              <span className="dgl-file-btn">Choose File</span>
              <span className="dgl-file-name">
                {editFile ? editFile.name : "No file Choosen"}
              </span>
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => setEditFile(e.target.files?.[0] || null)}
              />
            </label>

            <button className="dgl-gallery-btn" onClick={handleUpdateGallery}>
              Update Gallery
            </button>
          </div>
        </div>
      )}

      {/* ---------------- Delete Modal ---------------- */}
      {deleteTarget && (
        <div className="dgl-modal-overlay" onClick={closeDeleteModal}>
          <div className="dgl-modal-card dgl-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dgl-delete-icon">
            <img
              src="/assets/dbg_icon_07.png"
              alt="Delete Warning"
              className="dgl-delete-icon-img"
            />
          </div>
            <h2>Delete Information</h2>
            <p>
              {deleteTarget.type === "image"
                ? "This image will be deleted"
                : "This event and all its images will be deleted"}
            </p>
            <div className="dgl-delete-actions">
              <button className="dgl-btn-keep" onClick={closeDeleteModal}>
                No, Keep It
              </button>
              <button className="dgl-btn-delete" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}